import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    CardActionArea,
    Breadcrumbs,
    CircularProgress,
    Alert,
    Chip
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import SportsIcon from '@mui/icons-material/Sports';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { reportAPI } from '../../services/reportAPI';

interface ReportItem {
    id: string;
    name: string;
    description: string;
    path: string;
    icon: React.ReactNode;
    color: string;
    lastAccessed?: string;
    accessedBy?: string;
}

const ReportsPage: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reportAccess, setReportAccess] = useState<Record<string, { lastAccessed: string; accessedBy: string }>>({});

    const reports: ReportItem[] = [
        {
            id: 'management-report',
            name: 'Management Report',
            description: 'Member metrics, onboarding status, and membership tier distribution',
            path: '/management-report',
            icon: <PeopleIcon fontSize="large" />,
            color: 'primary.main'
        },
        {
            id: 'coaching-report',
            name: 'Coaching Report',
            description: 'Coaching sessions, programs, and coach performance analytics',
            path: '/coaching-report',
            icon: <SportsIcon fontSize="large" />,
            color: 'success.main'
        },
        {
            id: 'financial-report',
            name: 'Financial Report',
            description: 'Revenue analysis, transaction summaries, and financial trends',
            path: '/financial-report',
            icon: <AttachMoneyIcon fontSize="large" />,
            color: 'warning.main'
        },
        {
            id: 'accounting-report',
            name: 'Accounting Report',
            description: 'Detailed transaction records and accounting reconciliation',
            path: '/accounting-report',
            icon: <AccountBalanceIcon fontSize="large" />,
            color: 'info.main'
        },
        {
            id: 'arrears-report',
            name: 'Arrears Report',
            description: 'Members with failed payment transactions and outstanding balances',
            path: '/arrears-report',
            icon: <ReceiptIcon fontSize="large" />,
            color: 'error.main'
        }
    ];

    useEffect(() => {
        loadReportAccessData();
    }, []);

    const loadReportAccessData = async () => {
        try {
            setLoading(true);
            // Fetch recent report access from audit logs
            const response = await reportAPI.getReportAccessHistory();
            
            if (response.data && Array.isArray(response.data)) {
                const accessMap: Record<string, { lastAccessed: string; accessedBy: string }> = {};
                
                response.data.forEach((entry: any) => {
                    const reportId = entry.reportId;
                    if (reportId && (!accessMap[reportId] || new Date(entry.timestamp) > new Date(accessMap[reportId].lastAccessed))) {
                        accessMap[reportId] = {
                            lastAccessed: entry.timestamp,
                            accessedBy: entry.userName || 'Unknown User'
                        };
                    }
                });
                
                setReportAccess(accessMap);
            }
        } catch (err) {
            console.error('Error loading report access data:', err);
            // Don't show error to user - just won't display last accessed info
        } finally {
            setLoading(false);
        }
    };

    const handleReportClick = async (report: ReportItem) => {
        try {
            // Log report access
            await reportAPI.logReportAccess(report.id);
        } catch (err) {
            console.error('Error logging report access:', err);
            // Continue navigation even if logging fails
        }
        
        navigate(report.path);
    };

    const formatLastAccessed = (dateString?: string): string => {
        if (!dateString) return 'Never accessed';
        
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <Typography color="text.primary">Reports</Typography>
            </Breadcrumbs>

            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Reports
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Access analytical reports and insights
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                {reports.map((report) => {
                    const access = reportAccess[report.id];
                    
                    return (
                        <Grid key={report.id} size={{ xs: 12, sm: 6, md: 4 }}>
                            <Card elevation={2} sx={{ height: '100%' }}>
                                <CardActionArea 
                                    onClick={() => handleReportClick(report)}
                                    sx={{ height: '100%' }}
                                >
                                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <Box display="flex" alignItems="center" mb={2}>
                                            <Box 
                                                sx={{ 
                                                    color: report.color,
                                                    mr: 2,
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                {report.icon}
                                            </Box>
                                            <Typography variant="h6" component="div">
                                                {report.name}
                                            </Typography>
                                        </Box>
                                        
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary" 
                                            sx={{ mb: 2, flexGrow: 1 }}
                                        >
                                            {report.description}
                                        </Typography>
                                        
                                        {access && (
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Last accessed: {formatLastAccessed(access.lastAccessed)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    by {access.accessedBy}
                                                </Typography>
                                            </Box>
                                        )}
                                        
                                        {!access && !loading && (
                                            <Chip 
                                                label="Not yet accessed" 
                                                size="small" 
                                                variant="outlined"
                                                sx={{ alignSelf: 'flex-start' }}
                                            />
                                        )}
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </Container>
    );
};

export default ReportsPage;
